/**
 * CommunicationLogModal — single modal used everywhere we capture
 * a logged contact (patient detail, PCW detail, post-call popup,
 * standalone log page).
 *
 * Props:
 *   entityType       'patient' | 'pcw'   (required)
 *   entityId         string              (required)
 *   entityName       string              (optional — shown in header)
 *   defaults         object              (optional — pre-fills the form,
 *                                         used by the post-call modal to
 *                                         hand in caller phone/source SID)
 *   onClose()                            (required)
 *   onSaved(entry)                       (optional)
 *
 * Field coverage (matches Dawn's spec):
 *   channel · direction · category · disposition · contact_date · subject
 *   summary · follow_up_date · follow_up_assignee_id
 *
 * Posts to POST /api/communications which handles entity_type routing,
 * audit logging, EHR chart publishing, and follow-up Task creation.
 */
import { useEffect, useState } from "react";
import { X, Loader2, Plus, Phone, MessageSquare, Mail, FileText, Users, PhoneOff } from "lucide-react";
import { toast } from "sonner";
import NoteTemplateChips from "../comms/NoteTemplateChips";
import { appendNoteTemplate } from "../../lib/callNoteTemplates";

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "/logo-avida-yellow.png";

const CHANNELS = [
  { key: "call", label: "Call", icon: Phone },
  { key: "text", label: "Text", icon: MessageSquare },
  { key: "voicemail", label: "Voicemail", icon: PhoneOff },
  { key: "email", label: "Email", icon: Mail },
  { key: "in_person", label: "In Person", icon: Users },
  { key: "fax", label: "Fax", icon: FileText },
  { key: "other", label: "Other", icon: FileText },
];

// Mirrors backend CATEGORIES order so the dropdown matches Dawn's verbal list.
const CATEGORY_LABELS = {
  information_request: "Information Request",
  question: "Question",
  change_in_condition: "Change in Condition",
  payroll: "Payroll",
  billing: "Billing",
  scheduling: "Scheduling",
  clinical: "Clinical",
  auth: "Authorization",
  complaint: "Complaint",
  other: "Other",
};
const DISPOSITION_LABELS = {
  resolved: "Resolved",
  callback_requested: "Callback Requested",
  voicemail_left: "Left Voicemail",
  no_answer: "No Answer",
  wrong_number: "Wrong Number",
  escalated: "Escalated",
  in_progress: "In Progress",
};

export default function CommunicationLogModal({
  entityType,
  entityId,
  entityName,
  defaults = {},
  onClose,
  onSaved,
}) {
  const [channel, setChannel] = useState(defaults.channel || "call");
  const [direction, setDirection] = useState(defaults.direction || "inbound");
  const [contactDate, setContactDate] = useState(
    defaults.contact_date || new Date().toISOString().slice(0, 10)
  );
  const [subject, setSubject] = useState(defaults.subject || "");
  const [summary, setSummary] = useState(defaults.summary || "");
  const [category, setCategory] = useState(defaults.category || "");
  const [disposition, setDisposition] = useState(defaults.disposition || "");
  const [followUpDate, setFollowUpDate] = useState(defaults.follow_up_date || "");
  const [followUpAssignee, setFollowUpAssignee] = useState(defaults.follow_up_assignee_id || "");
  const [saving, setSaving] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [fromPartyName, setFromPartyName] = useState(defaults.from_party_name || "");
  const [fromPartyPhone, setFromPartyPhone] = useState(defaults.from_party_phone || "");

  // Fetch the assignee pool once on mount. Used for the follow-up "Assign to" dropdown.
  useEffect(() => {
    fetch(`${API}/api/admin-setup/users-roles`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setTeamMembers(Array.isArray(d) ? d : d?.users || []))
      .catch(() => setTeamMembers([]));
  }, []);

  const handleSave = async () => {
    if (!summary.trim()) {
      toast.error("Please write a brief summary of the conversation");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        entity_type: entityType,
        entity_id: entityId,
        channel,
        direction,
        contact_date: contactDate,
        subject,
        summary,
        category: category || null,
        disposition: disposition || null,
        follow_up_date: followUpDate || null,
        follow_up_assignee_id: followUpAssignee || null,
        from_party_name: fromPartyName || null,
        from_party_phone: fromPartyPhone || null,
        source_call_sid: defaults.source_call_sid || null,
        source_message_sid: defaults.source_message_sid || null,
      };
      const r = await fetch(`${API}/api/communications`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const detail = (await r.json().catch(() => ({})))?.detail || "Save failed";
        throw new Error(detail);
      }
      const entry = await r.json();
      toast.success(
        followUpDate && followUpAssignee
          ? "Contact logged + follow-up task created"
          : "Contact logged"
      );
      onSaved?.(entry);
    } catch (e) {
      toast.error(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      data-testid="comm-log-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-200 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div
          className="px-5 py-3 flex items-center justify-between border-b-[3px] shrink-0"
          style={{ borderColor: "#FFDA00", background: "linear-gradient(90deg, #032866 0%, #001A4D 100%)" }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <img src={LOGO_URL} alt="" className="w-7 h-7 rounded-md object-contain bg-white p-0.5" />
            <div className="min-w-0">
              <h3 className="text-sm font-black text-white truncate">
                Log a contact {entityName ? `· ${entityName}` : ""}
              </h3>
              <p className="text-[10px] font-medium text-white/70 uppercase tracking-wider">
                {entityType === "pcw" ? "PCW timeline" : entityType === "contact" ? "Contact timeline" : entityType === "unknown" ? "Unassigned queue" : "Client timeline"}
              </p>
            </div>
          </div>
          <button onClick={onClose} data-testid="comm-modal-close">
            <X className="w-5 h-5 text-white/80 hover:text-white" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 overflow-y-auto">
          {/* Channel pill grid */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#032866" }}>
              Channel
            </label>
            <div className="grid grid-cols-4 gap-1.5 mt-1.5">
              {CHANNELS.map((c) => {
                const Icon = c.icon;
                const active = channel === c.key;
                return (
                  <button
                    key={c.key}
                    onClick={() => setChannel(c.key)}
                    type="button"
                    className="flex items-center gap-1.5 px-2.5 py-2 rounded-md text-[10px] font-bold border transition"
                    style={
                      active
                        ? { background: "#032866", color: "#FFDA00", borderColor: "#032866" }
                        : { background: "#fff", color: "#032866", borderColor: "#e2e8f0" }
                    }
                    data-testid={`comm-channel-${c.key}`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Direction + Date */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#032866" }}>
                Direction
              </label>
              <div className="grid grid-cols-2 gap-1 mt-1">
                {[
                  { k: "inbound", l: "Inbound" },
                  { k: "outbound", l: "Outbound" },
                ].map((d) => {
                  const active = direction === d.k;
                  return (
                    <button
                      key={d.k}
                      onClick={() => setDirection(d.k)}
                      type="button"
                      className="px-2 py-1.5 rounded-md text-[10px] font-bold border transition"
                      style={
                        active
                          ? { background: "#032866", color: "#FFDA00", borderColor: "#032866" }
                          : { background: "#fff", color: "#032866", borderColor: "#e2e8f0" }
                      }
                      data-testid={`comm-direction-${d.k}`}
                    >
                      {d.l}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#032866" }}>
                Contact Date
              </label>
              <input
                type="date"
                value={contactDate}
                onChange={(e) => setContactDate(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-[11px]"
                data-testid="comm-date"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#032866" }}>
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-[11px] bg-white"
                data-testid="comm-category"
              >
                <option value="">— Pick one —</option>
                {Object.entries(CATEGORY_LABELS).map(([k, l]) => (
                  <option key={k} value={k}>{l}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#032866" }}>
              Subject (optional)
            </label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Schedule change request"
              className="mt-1 w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-[11px]"
              data-testid="comm-subject"
            />
          </div>

          {/* Summary */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#032866" }}>
              Summary <span className="text-red-500 normal-case">*</span>
            </label>
            <NoteTemplateChips className="mt-1.5" onPick={(t) => setSummary((d) => appendNoteTemplate(d, t))} />
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={5}
              placeholder="What was discussed? What action was taken? Concerns? Change in condition? Payroll question?"
              className="mt-1 w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-[11px]"
              data-testid="comm-summary"
            />
          </div>

          {/* From-party (caller ID) — auto-populated by post-call modal */}
          {(fromPartyPhone || fromPartyName || defaults.source_call_sid) && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#032866" }}>
                  Caller Name
                </label>
                <input
                  value={fromPartyName}
                  onChange={(e) => setFromPartyName(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-[11px]"
                  data-testid="comm-from-name"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#032866" }}>
                  Caller Phone
                </label>
                <input
                  value={fromPartyPhone}
                  onChange={(e) => setFromPartyPhone(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-[11px] font-mono"
                  data-testid="comm-from-phone"
                />
              </div>
            </div>
          )}

          {/* Disposition */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#032866" }}>
              Disposition (outcome)
            </label>
            <select
              value={disposition}
              onChange={(e) => setDisposition(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-[11px] bg-white"
              data-testid="comm-disposition"
            >
              <option value="">— Pick one —</option>
              {Object.entries(DISPOSITION_LABELS).map(([k, l]) => (
                <option key={k} value={k}>{l}</option>
              ))}
            </select>
          </div>

          {/* Follow-up */}
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
            <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: "#032866" }}>
              Follow-up (optional — creates a task)
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-slate-600">Date</label>
                <input
                  type="date"
                  value={followUpDate}
                  onChange={(e) => setFollowUpDate(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-[11px]"
                  data-testid="comm-follow-up-date"
                />
              </div>
              <div>
                <label className="text-[10px] text-slate-600">Assign to</label>
                <select
                  value={followUpAssignee}
                  onChange={(e) => setFollowUpAssignee(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-[11px] bg-white"
                  data-testid="comm-follow-up-assignee"
                >
                  <option value="">— Pick one —</option>
                  {teamMembers.map((m) => (
                    <option key={m.id} value={m.id}>
                      {(m.first_name || "") + " " + (m.last_name || "")} {m.email ? `(${m.email})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {followUpDate && !followUpAssignee && (
              <p className="text-[10px] text-amber-700 mt-1.5">
                Pick an assignee or a task won&apos;t be created.
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-100 shrink-0 bg-slate-50">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-[11px] font-bold rounded-md text-slate-500 hover:bg-slate-100"
            data-testid="comm-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-1.5 text-[11px] font-black rounded-md shadow-sm hover:shadow-md transition disabled:opacity-50 flex items-center gap-1.5"
            style={{ background: "#032866", color: "#FFDA00" }}
            data-testid="comm-save"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            {saving ? "Saving..." : "Log contact"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Export the lookup tables so the timeline list view can render
// matching badges without re-defining them.
export { CATEGORY_LABELS, DISPOSITION_LABELS, CHANNELS };
